import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import OfficeOnlineViewer from '../../../components/OfficeOnlineViewer';
import Button from '../../../components/ui/Button';
import { documentService } from '../../../services/documentService';

// Configure PDF.js worker - use local worker from installed package
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const DocumentEditor = ({
  document,
  onSave,
  saving,
  documentType,
  content,
  onChange,
  highlightLocation,
  onHighlightCleared
}) => {
  const [localContent, setLocalContent] = useState(content || '');
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [contentError, setContentError] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [parsedHtml, setParsedHtml] = useState('');
  const [displayMode, setDisplayMode] = useState('formatted'); // 'formatted' or 'raw'
  const [viewerMode, setViewerMode] = useState('enhanced'); // 'enhanced', 'office-online', 'basic'
  const [authRequired, setAuthRequired] = useState(false);
  const editorRef = useRef(null);
  const highlightTimeoutRef = useRef(null);

  // Determine if document supports Office Online
  const supportsOfficeOnline = (document) => {
    if (!document?.mimeType) return false;

    const supportedTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'application/msword', // .doc
      'application/vnd.ms-excel', // .xls
      'application/vnd.ms-powerpoint' // .ppt
    ];

    return supportedTypes?.includes(document?.mimeType);
  };

  // Auto-select best viewer mode based on document type - Office Online as default
  useEffect(() => {
    if (document) {
      // For legacy .doc files, force Office Online mode (they can't be parsed with mammoth)
      if (document?.mimeType === 'application/msword') {
        const fileName = document?.name || '';
        const fileExtension = fileName?.split('.')?.pop()?.toLowerCase();
        if (fileExtension === 'doc') {
          setViewerMode('office-online'); // Force Office Online for .doc files
          return;
        }
      }
      
      if (supportsOfficeOnline(document)) {
        setViewerMode('office-online'); // Default to Office Online for supported documents
      } else if (document?.mimeType === 'application/pdf' ||
                 document?.mimeType?.includes('word') ||
                 document?.mimeType?.includes('document')) {
        setViewerMode('enhanced');
      } else {
        setViewerMode('basic');
      }
    }
  }, [document]);

  // Load actual document content from file
  useEffect(() => {
    if (document && document?.filePath && (viewerMode === 'enhanced' || viewerMode === 'basic')) {
      loadDocumentContent();
    }
  }, [document, viewerMode]);

  const parseWordDocumentWithFormatting = async (content) => {
    try {
      const arrayBuffer = await content?.arrayBuffer();

      // Enhanced mammoth options for better list formatting
      const options = {
        convertImage: mammoth?.images?.imgElement(function(image) {
          return image?.read("base64")?.then(function(imageBuffer) {
            return {
              src: "data:" + image?.contentType + ";base64," + imageBuffer
            };
          });
        }),
        styleMap: [
          "p[style-name='Heading 1'] => h1:fresh",
          "p[style-name='Heading 2'] => h2:fresh",
          "p[style-name='Heading 3'] => h3:fresh",
          "p[style-name='Heading 4'] => h4:fresh",
          "p[style-name='Heading 5'] => h5:fresh",
          "p[style-name='Heading 6'] => h6:fresh",
          "p[style-name='List Paragraph'] => li:fresh",
          "p[style-name='ListParagraph'] => li:fresh",
          "p[style-name='Normal List'] => li:fresh",
          "p[style-name='Bullet List'] => li:fresh",
          "p[style-name='Number List'] => li:fresh",
          "r[style-name='Strong'] => strong",
          "r[style-name='Emphasis'] => em",
          "p[style-name='Quote'] => blockquote:fresh"
        ],
        includeDefaultStyleMap: true,
        includeEmbeddedStyleMap: true,
        transformDocument: function(document) {
          return document;
        }
      };

      const result = await mammoth?.convertToHtml({ arrayBuffer }, options);

      if (result?.messages?.length > 0) {
        console.warn('Mammoth conversion messages:', result?.messages);
      }

      let enhancedHtml = result?.value || '';

      // Enhanced list formatting
      enhancedHtml = enhancedHtml?.replace(/<p([^>]*)>(\s*)(•|·|‒|–|—|\*|\+|-|▪|▫|■|□|◦|◆|◇|○|●|►|▸|▹|☐|☑|✓|✔|⚫|⚪)(\s*)([^<]+)<\/p>/g,
        '<li class="bullet-item" data-marker="•">$5</li>');

      enhancedHtml = enhancedHtml?.replace(/<p([^>]*)>(\s*)(\d+\.)(\s*)([^<]+)<\/p>/g,
        '<li class="numbered-item" data-marker="$3">$5</li>');

      enhancedHtml = enhancedHtml?.replace(/(<li class="bullet-item"[^>]*>.*?<\/li>(\s*<li class="bullet-item"[^>]*>.*?<\/li>)*)/gs,
        '<ul class="bullet-list">$1</ul>');

      enhancedHtml = enhancedHtml?.replace(/(<li class="numbered-item"[^>]*>.*?<\/li>(\s*<li class="numbered-item"[^>]*>.*?<\/li>)*)/gs,
        '<ol class="numbered-list">$1</ol>');

      return {
        html: enhancedHtml,
        text: (await mammoth?.extractRawText({ arrayBuffer }))?.value || '',
        success: true
      };
    } catch (error) {
      console.error('Error parsing Word document with mammoth:', error);
      return {
        html: '',
        text: `Error parsing Word document: ${error?.message}`,
        success: false
      };
    }
  };

  const parsePdfDocument = async (content) => {
    try {
      const arrayBuffer = await content?.arrayBuffer();
      const pdf = await pdfjsLib?.getDocument({ data: arrayBuffer })?.promise;
      let fullText = '';
      let htmlContent = '<div class="pdf-content">';

      for (let pageNum = 1; pageNum <= pdf?.numPages; pageNum++) {
        const page = await pdf?.getPage(pageNum);
        const textContent = await page?.getTextContent();

        let pageText = '';
        let pageHtml = `<div class="pdf-page" data-page="${pageNum}">
          <div class="pdf-page-header">Page ${pageNum}</div>`;

        textContent?.items?.forEach((item) => {
          if (item?.str?.trim()) {
            pageText += item?.str + ' ';
            pageHtml += `<span style="font-size: ${item?.height}px;">${item?.str}</span> `;
          }
        });

        fullText += `\n--- Page ${pageNum} ---\n${pageText}\n`;
        pageHtml += '</div>';
        htmlContent += pageHtml;
      }

      htmlContent += '</div>';

      return {
        html: htmlContent,
        text: fullText,
        success: true,
        pageCount: pdf?.numPages
      };
    } catch (error) {
      console.error('Error parsing PDF document:', error);
      return {
        html: '',
        text: `Error parsing PDF document: ${error?.message}`,
        success: false
      };
    }
  };

  const loadDocumentContent = async () => {
    try {
      setLoadingContent(true);
      setContentError('');
      setIsParsing(true);
      setParsedHtml('');

      console.log('[EDITOR] Loading document content for:', {
        id: document?.id,
        name: document?.name,
        filePath: document?.filePath,
        storageType: document?.storageType || document?.storage_type,
        sharePointItemId: document?.sharePointItemId || document?.sharepoint_item_id
      });

      // CRITICAL: Pass all storage metadata to getDocumentContent
      const content = await documentService?.getDocumentContent(
        document?.id,
        document?.filePath,
        document?.mimeType,
        document?.storageType || document?.storage_type || 'supabase',
        document?.sharePointItemId || document?.sharepoint_item_id
      );

      let extractedText = '';
      let htmlContent = '';

      if (document?.mimeType === 'text/plain') {
        extractedText = content;
        htmlContent = `<pre class="text-content">${content}</pre>`;
      } else if (document?.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        // .docx files - can be parsed with mammoth
        const parseResult = await parseWordDocumentWithFormatting(content);

        if (parseResult?.success) {
          extractedText = parseResult?.text;
          htmlContent = parseResult?.html;
        } else {
          extractedText = parseResult?.text || `Error parsing Word document: ${document?.name}`;
          htmlContent = `<div class="error-content">${extractedText}</div>`;
        }
      } else if (document?.mimeType === 'application/msword') {
        // .doc files (legacy binary format) - cannot be parsed with mammoth
        // These need to be opened in Office Online or downloaded
        const fileName = document?.name || 'document.doc';
        const fileExtension = fileName?.split('.')?.pop()?.toLowerCase();
        
        if (fileExtension === 'doc') {
          // Legacy .doc format - show message to use Office Online
          extractedText = `Legacy Word Document (.doc)\n\nThis document is in the legacy Microsoft Word format (.doc) which cannot be parsed in the browser.\n\nTo view and edit this document:\n1. Switch to "Office Online" mode using the viewer selector above\n2. Or download the document and open it in Microsoft Word\n\nDocument Details:\n- File: ${document?.name}\n- Type: Legacy Word Document (.doc)\n- Size: ${document?.size ? Math.round(document?.size / 1024) + ' KB' : 'Unknown'}\n- MIME Type: ${document?.mimeType}\n\nNote: Office Online editing is recommended for this document type.`;
          htmlContent = `<div class="binary-content">
            <div class="info-banner">
              <h3>Legacy Word Document Format</h3>
              <p>This document uses the legacy .doc format which cannot be parsed in the browser.</p>
              <p><strong>To view and edit:</strong></p>
              <ul>
                <li>Switch to <strong>"Office Online"</strong> mode using the viewer selector above</li>
                <li>Or download the document and open it in Microsoft Word</li>
              </ul>
              <p><em>Office Online editing is recommended for this document type.</em></p>
            </div>
            <div class="document-info">
              <p><strong>File:</strong> ${document?.name}</p>
              <p><strong>Type:</strong> Legacy Word Document (.doc)</p>
              <p><strong>Size:</strong> ${document?.size ? Math.round(document?.size / 1024) + ' KB' : 'Unknown'}</p>
            </div>
          </div>`;
        } else {
          // Fallback for other msword types
          extractedText = `Word Document\n\nThis document cannot be parsed in the browser.\n\nPlease use Office Online mode or download the document.\n\nDocument: ${document?.name}`;
          htmlContent = `<div class="error-content">${extractedText}</div>`;
        }
      } else if (document?.mimeType === 'application/pdf') {
        const parseResult = await parsePdfDocument(content);

        if (parseResult?.success) {
          extractedText = parseResult?.text;
          htmlContent = parseResult?.html;
        } else {
          extractedText = parseResult?.text || `Error parsing PDF document: ${document?.name}`;
          htmlContent = `<div class="error-content">${extractedText}</div>`;
        }
      } else if (content && typeof content === 'string') {
        extractedText = content;
        htmlContent = `<pre class="text-content">${content}</pre>`;
      } else if (content) {
        try {
          extractedText = await content?.text();
          htmlContent = `<pre class="text-content">${extractedText}</pre>`;
        } catch (error) {
          extractedText = `Binary Document: ${document?.name}\n\nThis document contains binary data that cannot be displayed as text.\n\nDocument Details:\n- File: ${document?.name}\n- Type: ${document?.type || 'Binary'}\n- Size: ${document?.size ? Math.round(document?.size / 1024) + ' KB' : 'Unknown'}\n- MIME Type: ${document?.mimeType}\n\nTo view this document, please download it and open with the appropriate application.`;
          htmlContent = `<div class="binary-content">${extractedText}</div>`;
        }
      } else {
        extractedText = `Document: ${document?.name}\n\nUnable to load document content.\n\nDocument Details:\n- File: ${document?.name}\n- Type: ${document?.type || 'Unknown'}\n- Size: ${document?.size ? Math.round(document?.size / 1024) + ' KB' : 'Unknown'}\n\nPlease try downloading the document directly.`;
        htmlContent = `<div class="error-content">${extractedText}</div>`;
      }

      setLocalContent(extractedText);
      setParsedHtml(htmlContent);
      onChange?.(extractedText);
      setIsParsing(false);

    } catch (error) {
      console.error('Error loading document content:', error);
      setContentError(`Failed to load document content: ${error?.message}`);

      const fallbackContent = `Document: ${document?.name}\n\nError loading document content: ${error?.message}\n\nDocument Details:\n- File: ${document?.name}\n- Type: ${document?.type || 'Unknown'}\n- Size: ${document?.size ? Math.round(document?.size / 1024) + ' KB' : 'Unknown'}\n- Last Modified: ${document?.updatedAt ? new Date(document?.updatedAt)?.toLocaleString() : 'Unknown'}\n\nPlease try refreshing the page or downloading the document directly.`;

      setLocalContent(fallbackContent);
      setParsedHtml(`<div class="error-content">${fallbackContent}</div>`);
      onChange?.(fallbackContent);
      setIsParsing(false);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleRefreshContent = () => {
    if (document) {
      if (viewerMode === 'office-online') {
        // Office Online handles refresh internally
        return;
      }
      loadDocumentContent();
    }
  };

  // Handle viewer mode switching
  const handleViewerModeChange = (newMode) => {
    setViewerMode(newMode);
    setContentError('');

    // If switching to Office Online, don't load content locally
    if (newMode === 'office-online') {
      setLocalContent('');
      setParsedHtml('');
    }
  };

  // NEW: Handle closing Office Online viewer
  const handleOfficeOnlineClose = () => {
    // Switch back to enhanced view when Office Online viewer is closed
    handleViewerModeChange('enhanced');
  };

  // Handle Office Online authentication requirement
  const handleAuthRequired = () => {
    setAuthRequired(true);
  };

  // Handle Office Online errors
  const handleOfficeOnlineError = (error) => {
    console.error('Office Online error:', error);
    setContentError(`Office Online error: ${error?.message}`);

    // Fallback to enhanced viewer on Office Online failure
    if (document?.mimeType?.includes('word') || document?.mimeType?.includes('document')) {
      setViewerMode('enhanced');
    }
  };

  // Handle highlight location from AI Analysis
  useEffect(() => {
    if (highlightLocation && editorRef?.current) {
      if (highlightTimeoutRef?.current) {
        clearTimeout(highlightTimeoutRef?.current);
      }

      console.log('Highlighting location:', highlightLocation);

      const highlightIndicator = document?.createElement('div');
      highlightIndicator.className = 'fixed top-20 right-6 bg-warning text-warning-foreground px-3 py-2 rounded-lg shadow-lg z-50 animate-pulse';
      highlightIndicator.innerHTML = `
        <div class="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
          <span class="text-sm font-medium">Location: ${highlightLocation}</span>
          <button onClick="this.parentElement.parentElement.remove()" class="ml-2 hover:bg-warning/20 rounded p-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      `;

      document?.body?.appendChild(highlightIndicator);

      highlightTimeoutRef.current = setTimeout(() => {
        if (highlightIndicator?.parentElement) {
          highlightIndicator?.remove();
        }
        onHighlightCleared?.();
      }, 10000);
    }

    return () => {
      if (highlightTimeoutRef?.current) {
        clearTimeout(highlightTimeoutRef?.current);
      }
    };
  }, [highlightLocation, onHighlightCleared]);

  // Handle content changes
  const handleContentChange = (e) => {
    const newContent = e?.target?.value;
    setLocalContent(newContent);
    onChange?.(newContent);
    setHasUnsavedChanges(true);
  };

  // Handle save
  const handleSave = async () => {
    try {
      await onSave?.(localContent);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save document:', error);
    }
  };

  useEffect(() => {
    if (hasUnsavedChanges && !saving && viewerMode !== 'office-online') {
      const autoSaveTimer = setTimeout(() => {
        handleSave();
      }, 5000);

      return () => clearTimeout(autoSaveTimer);
    }
  }, [localContent, hasUnsavedChanges, saving, viewerMode]);

  if (!document) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/20">
        <div className="text-center">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Document Selected</h3>
          <p className="text-muted-foreground">
            Select a document from the sidebar to start editing.
          </p>
        </div>
      </div>
    );
  }

  // Office Online Viewer Mode
  if (viewerMode === 'office-online' && supportsOfficeOnline(document)) {
    // Prepare document object with all necessary properties for Office Online
    const officeOnlineDocument = {
      id: document?.id,
      name: document?.name,
      filePath: document?.filePath,
      mimeType: document?.mimeType,
      size: document?.size || document?.fileSize,
      type: document?.type,
      // Ensure we have content or file path for Office Online
      content: document?.content,
      file: document?.file
    };

    return (
      <div className="h-full flex flex-col bg-background">
        {/* Enhanced Header with Mode Switcher */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">{document?.name}</h2>
              <p className="text-sm text-muted-foreground">
                Office Online Integration • Full editing capabilities
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center bg-muted rounded-lg p-1">
                <Button
                  variant={viewerMode === 'office-online' ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleViewerModeChange('office-online')}
                  disabled={!supportsOfficeOnline(document)}
                >
                  Office Online
                </Button>
                <Button
                  variant={viewerMode === 'enhanced' ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleViewerModeChange('enhanced')}
                >
                  Enhanced View
                </Button>
                <Button
                  variant={viewerMode === 'basic' ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleViewerModeChange('basic')}
                >
                  Basic View
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <OfficeOnlineViewer
            document={officeOnlineDocument}
            documentData={officeOnlineDocument}
            onClose={handleOfficeOnlineClose}
            onSave={async (saveData) => {
              // Handle save from Office Online
              if (saveData?.saved) {
                // Document was saved in Office Online
                // Optionally sync back to ZentriDMS if needed
                await onSave?.(saveData);
              } else {
                await onSave?.(saveData);
              }
            }}
            saving={saving}
            mode="edit"
            onError={handleOfficeOnlineError}
            onAuthRequired={handleAuthRequired}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Editor Header with Mode Switcher */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{document?.name}</h2>
            <p className="text-sm text-muted-foreground">
              {documentType} • {hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'}
              {loadingContent && ' • Loading content...'}
              {isParsing && ' • Parsing document...'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Viewer Mode Switcher */}
            <div className="flex items-center bg-muted rounded-lg p-1 mr-2">
              {supportsOfficeOnline(document) && (
                <Button
                  variant={viewerMode === 'office-online' ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleViewerModeChange('office-online')}
                  iconName="ExternalLink"
                  iconPosition="left"
                  iconSize={12}
                >
                  Office Online
                </Button>
              )}
              <Button
                variant={viewerMode === 'enhanced' ? "default" : "ghost"}
                size="sm"
                onClick={() => handleViewerModeChange('enhanced')}
              >
                Enhanced
              </Button>
              <Button
                variant={viewerMode === 'basic' ? "default" : "ghost"}
                size="sm"
                onClick={() => handleViewerModeChange('basic')}
              >
                Basic
              </Button>
            </div>

            {contentError && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshContent}
                iconName="RefreshCw"
                iconPosition="left"
                iconSize={14}
              >
                Retry
              </Button>
            )}

            {parsedHtml && !isEditing && viewerMode === 'enhanced' && (
              <Button
                variant={displayMode === 'formatted' ? "default" : "outline"}
                size="sm"
                onClick={() => setDisplayMode(displayMode === 'formatted' ? 'raw' : 'formatted')}
                iconName={displayMode === 'formatted' ? "Code" : "Eye"}
                iconPosition="left"
                iconSize={14}
              >
                {displayMode === 'formatted' ? 'Raw Text' : 'Formatted'}
              </Button>
            )}

            {hasUnsavedChanges && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                iconName={saving ? "Loader2" : "Save"}
                iconPosition="left"
                iconSize={14}
                className={saving ? "animate-spin" : ""}
              >
                {saving ? 'Saving...' : 'Save Now'}
              </Button>
            )}

            <Button
              variant={isEditing ? "default" : "outline"}
              size="sm"
              onClick={() => setIsEditing(!isEditing)}
              iconName={isEditing ? "Eye" : "Edit"}
              iconPosition="left"
              iconSize={14}
              disabled={loadingContent}
            >
              {isEditing ? 'Preview' : 'Edit'}
            </Button>
          </div>
        </div>
      </div>

      {/* Office Online Priority Banner - Updated messaging */}
      {supportsOfficeOnline(document) && viewerMode !== 'office-online' && (
        <div className="flex-shrink-0 px-6 py-3 bg-blue-50 border-b border-blue-200">
          <div className="flex items-center gap-2 text-blue-800">
            <Icon name="Info" size={16} />
            <span className="text-sm">
              <strong>Office Online editing is recommended</strong> for this document type.
              <button
                onClick={() => handleViewerModeChange('office-online')}
                className="ml-1 underline hover:no-underline font-medium"
              >
                Learn about Office Online options
              </button>
              {' '}to see how you can edit this document with full Office capabilities.
            </span>
          </div>
        </div>
      )}

      {/* Content Error Banner */}
      {contentError && (
        <div className="flex-shrink-0 px-6 py-3 bg-destructive/10 border-b border-destructive/20">
          <div className="flex items-center gap-2 text-destructive">
            <Icon name="AlertTriangle" size={16} />
            <span className="text-sm">{contentError}</span>
            <button
              onClick={() => setContentError('')}
              className="ml-auto hover:bg-destructive/20 rounded p-1"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Editor Content */}
      <div className="flex-1 overflow-hidden">
        {loadingContent ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Icon name="Loader2" size={32} className="animate-spin text-primary mb-4 mx-auto" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {isParsing ? 'Parsing Document...' : 'Loading Document...'}
              </h3>
              <p className="text-muted-foreground">
                {isParsing ? 'Extracting content with formatting from document file' : 'Downloading document from storage'}
              </p>
            </div>
          </div>
        ) : isEditing ? (
          <div className="h-full p-6">
            <textarea
              ref={editorRef}
              value={localContent}
              onChange={handleContentChange}
              placeholder="Start typing your document content..."
              className="w-full h-full resize-none border border-border rounded-lg p-4 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              style={{
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '14px',
                lineHeight: '1.6'
              }}
            />
          </div>
        ) : (
          <div className="h-full overflow-auto p-6">
            <div
              className="prose prose-sm max-w-none text-foreground"
              style={{
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '14px',
                lineHeight: '1.6'
              }}
            >
              {localContent ? (
                (viewerMode === 'enhanced' && displayMode === 'formatted' && parsedHtml) ? (
                  <div
                    className="formatted-content"
                    dangerouslySetInnerHTML={{ __html: parsedHtml }}
                    style={{
                      '--pdf-page-bg': '#fafafa',
                      '--pdf-page-border': '#e0e0e0'
                    }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-foreground">
                    {localContent}
                  </pre>
                )
              ) : (
                <div className="text-center py-12">
                  <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">Document is Empty</h3>
                  <p className="text-muted-foreground mb-4">
                    This document doesn't have any content yet.
                  </p>
                  <div className="flex gap-2 justify-center">
                    {supportsOfficeOnline(document) ? (
                      <Button
                        variant="default"
                        onClick={() => handleViewerModeChange('office-online')}
                        iconName="ExternalLink"
                        iconPosition="left"
                        iconSize={16}
                      >
                        Edit with Office Online
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => setIsEditing(true)}
                        iconName="Edit"
                        iconPosition="left"
                        iconSize={16}
                      >
                        Start Editing
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={handleRefreshContent}
                      iconName="RefreshCw"
                      iconPosition="left"
                      iconSize={16}
                    >
                      Reload Content
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="flex-shrink-0 px-6 py-2 border-t border-border bg-muted/30 text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span>Mode: {viewerMode === 'enhanced' ? 'Enhanced' : 'Basic'} View</span>
            {localContent && (
              <>
                <span>Characters: {localContent?.length}</span>
                <span>Words: {localContent?.split(/\s+/)?.filter(word => word?.length > 0)?.length}</span>
                <span>Lines: {localContent?.split('\n')?.length}</span>
              </>
            )}
            {parsedHtml && displayMode === 'formatted' && viewerMode === 'enhanced' && (
              <span className="text-green-600">• Formatted View</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {saving && (
              <div className="flex items-center gap-1">
                <Icon name="Loader2" size={12} className="animate-spin" />
                <span>Saving...</span>
              </div>
            )}
            {loadingContent && (
              <div className="flex items-center gap-1">
                <Icon name="Loader2" size={12} className="animate-spin" />
                <span>{isParsing ? 'Parsing...' : 'Loading...'}</span>
              </div>
            )}
            {highlightLocation && (
              <div className="flex items-center gap-1 text-warning">
                <Icon name="MapPin" size={12} />
                <span>Highlighted: {highlightLocation}</span>
              </div>
            )}
            {supportsOfficeOnline(document) && viewerMode !== 'office-online' && (
              <div className="flex items-center gap-1 text-blue-600">
                <Icon name="ExternalLink" size={12} />
                <span>Office Online Recommended</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CSS Styles for formatted content */}
      <style>{`
        .formatted-content {
          font-family: 'Times New Roman', Times, serif;
          line-height: 1.6;
          color: #333;
          max-width: none;
        }

        .formatted-content h1, .formatted-content h2, .formatted-content h3,
        .formatted-content h4, .formatted-content h5, .formatted-content h6 {
          font-weight: bold;
          margin: 1.2em 0 0.6em 0;
          color: #000;
          border-bottom: 1px solid #e0e0e0;
          padding-bottom: 0.3em;
        }

        .formatted-content h1 { font-size: 24px; }
        .formatted-content h2 { font-size: 20px; }
        .formatted-content h3 { font-size: 18px; }
        .formatted-content h4 { font-size: 16px; }
        .formatted-content h5 { font-size: 14px; }
        .formatted-content h6 { font-size: 12px; }

        .formatted-content p {
          margin: 0.8em 0;
          text-align: justify;
          text-indent: 0;
        }

        .formatted-content blockquote {
          margin: 1em 0;
          padding: 0.5em 1em;
          border-left: 4px solid #d1d5db;
          background: #f9fafb;
          font-style: italic;
        }

        .formatted-content ul, .formatted-content ol,
        .formatted-content .bullet-list, .formatted-content .numbered-list,
        .formatted-content .roman-list, .formatted-content .letter-list,
        .formatted-content .parenthetical-list {
          margin: 1em 0;
          padding-left: 0;
          list-style: none;
        }

        .formatted-content .bullet-list {
          padding-left: 2em;
        }

        .formatted-content .numbered-list,
        .formatted-content .parenthetical-list,
        .formatted-content .letter-list {
          padding-left: 2.5em;
          counter-reset: list-counter;
        }

        .formatted-content .roman-list {
          padding-left: 3em;
        }

        .formatted-content .bullet-list .bullet-item,
        .formatted-content ul li {
          margin: 0.4em 0;
          position: relative;
          padding-left: 1.2em;
          text-indent: 0;
          display: block;
        }

        .formatted-content .bullet-list .bullet-item:before,
        .formatted-content ul li:before {
          content: "•";
          font-weight: bold;
          color: #333;
          font-size: 1.2em;
          position: absolute;
          left: 0;
          top: 0;
          width: 1em;
          text-align: center;
        }

        .formatted-content .bullet-list .bullet-item[data-marker]:before {
          content: attr(data-marker);
        }

        .formatted-content .numbered-list .numbered-item {
          margin: 0.4em 0;
          position: relative;
          padding-left: 1em;
          counter-increment: list-counter;
          text-indent: 0;
          display: block;
        }

        .formatted-content .numbered-list .numbered-item:before {
          content: counter(list-counter) ".";
          font-weight: bold;
          color: #333;
          position: absolute;
          left: -2.5em;
          top: 0;
          width: 2em;
          text-align: right;
        }

        .formatted-content .numbered-list .numbered-item[data-marker]:before {
          content: attr(data-marker);
        }

        .formatted-content hr,
        .formatted-content .section-divider {
          border: none;
          border-top: 2px solid #d1d5db;
          margin: 2em 0;
          width: 100%;
          clear: both;
          display: block;
          height: 2px;
          background: linear-gradient(to right, #d1d5db, #9ca3af, #d1d5db);
        }

        .formatted-content table,
        .formatted-content .document-table {
          border-collapse: collapse;
          width: 100%;
          margin: 1.5em 0;
          border: 2px solid #374151;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .formatted-content .document-table td,
        .formatted-content .document-table th,
        .formatted-content table td,
        .formatted-content table th {
          border: 1px solid #d1d5db;
          padding: 12px 16px;
          text-align: left;
          vertical-align: top;
        }

        .formatted-content .document-table th,
        .formatted-content table th {
          background-color: #f3f4f6;
          font-weight: bold;
          border-bottom: 2px solid #9ca3af;
        }

        .formatted-content .document-table tbody tr:nth-child(even),
        .formatted-content table tbody tr:nth-child(even) {
          background-color: #f9fafb;
        }

        .formatted-content .document-table tbody tr:hover,
        .formatted-content table tbody tr:hover {
          background-color: #f3f4f6;
        }

        .formatted-content strong, .formatted-content b {
          font-weight: bold;
          color: #1f2937;
        }

        .formatted-content em, .formatted-content i {
          font-style: italic;
          color: #374151;
        }

        .formatted-content u {
          text-decoration: underline;
          text-decoration-color: #6b7280;
        }

        .formatted-content s, .formatted-content strike {
          text-decoration: line-through;
          text-decoration-color: #ef4444;
        }

        .formatted-content img {
          max-width: 100%;
          height: auto;
          margin: 1em 0;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .pdf-content .pdf-page {
          background: var(--pdf-page-bg);
          border: 1px solid var(--pdf-page-border);
          margin: 1em 0;
          padding: 1.5em;
          border-radius: 6px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .pdf-content .pdf-page-header {
          font-weight: bold;
          font-size: 12px;
          color: #6b7280;
          border-bottom: 2px solid var(--pdf-page-border);
          padding-bottom: 0.75em;
          margin-bottom: 1.5em;
          text-align: center;
        }

        .text-content, .error-content, .binary-content {
          white-space: pre-wrap;
          font-family: 'Courier New', Courier, monospace;
          font-size: 13px;
          line-height: 1.5;
          padding: 1em;
          border-radius: 6px;
        }

        .error-content {
          color: #dc2626;
          background: #fee2e2;
          border: 1px solid #fecaca;
        }

        .binary-content {
          color: #6b7280;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
        }

        .binary-content .info-banner {
          background-color: #e3f2fd;
          border: 1px solid #90caf9;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }

        .binary-content .info-banner h3 {
          color: #1976d2;
          margin-top: 0;
          margin-bottom: 12px;
          font-size: 18px;
          font-weight: 600;
        }

        .binary-content .info-banner p {
          color: #424242;
          margin: 8px 0;
          line-height: 1.6;
        }

        .binary-content .info-banner ul {
          margin: 12px 0;
          padding-left: 24px;
        }

        .binary-content .info-banner li {
          margin: 6px 0;
          color: #424242;
        }

        .binary-content .info-banner strong {
          color: #1976d2;
        }

        .binary-content .info-banner em {
          color: #616161;
        }

        .binary-content .document-info {
          background-color: #f5f5f5;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          padding: 16px;
          margin-top: 16px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }

        .binary-content .document-info p {
          margin: 8px 0;
          color: #616161;
        }
      `}</style>
    </div>
  );
};

export default DocumentEditor;